import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpen,
  Brain,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Compass,
  GraduationCap,
  Lightbulb,
  Quote,
  Scale,
  SearchCheck,
  Sparkles,
  Target,
  Wrench,
  Zap,
} from 'lucide-react';
import Header from '../components/Header';
import FloatingIcons from '../components/FloatingIcons';

const RULE_GROUPS = [
  {
    number: '01',
    title: 'Think Before You Act',
    icon: Lightbulb,
    quotes: [
      {
        text: 'Garbage in, garbage out.',
        icon: Brain,
        explanation:
          'Vague context in, vague — and confidently wrong — output out. Same rule, just a faster feedback loop now.',
      },
      {
        text: 'Measure twice, cut once.',
        icon: Scale,
        explanation:
          'Two extra minutes shaping the prompt saves twenty spent re-generating and re-checking later.',
      },
      {
        text: "Give me six hours to chop down a tree and I'll spend the first four sharpening the axe.",
        icon: Wrench,
        explanation:
          "Building the context, examples, and constraints is the work now. Skip that, and you're swinging at full speed with a dull blade.",
      },
    ],
  },
  {
    number: '02',
    title: 'Verify Before You Ship',
    icon: SearchCheck,
    quotes: [
      {
        text: 'Trust, but verify.',
        icon: SearchCheck,
        explanation:
          "Use the output, don't worship it. Every answer gets read before it ships — same as a junior's first pull request.",
      },
      {
        text: 'A bad workman blames his tools.',
        icon: Wrench,
        explanation:
          'A weak result means checking what you gave it first — not the model.',
      },
      {
        text: 'The devil is in the details.',
        icon: Target,
        explanation:
          'AI nails the big picture and gets careless with the edge case. Closing that gap is still on us.',
      },
    ],
  },
  {
    number: '03',
    title: "Wisdom Isn't Automated",
    icon: GraduationCap,
    quotes: [
      {
        text: 'Practice makes perfect.',
        icon: BookOpen,
        explanation:
          "AI gives you more reps per hour, not a shortcut past building the underlying skill. Can't do it without AI? You don't know it yet.",
      },
      {
        text: 'The right tool for the right job.',
        icon: Compass,
        explanation:
          'Not every task needs the same model, or needs AI at all. Knowing when not to reach for it is still a skill.',
      },
    ],
  },
  {
    number: '04',
    title: 'Move Quick, Not Careless',
    icon: Zap,
    quotes: [
      {
        text: "Try and fail, don't fail to try.",
        icon: Sparkles,
        explanation:
          "AI makes trying cheap. There's no excuse left for not attempting the harder approach first.",
      },
      {
        text: 'Time is money.',
        icon: Target,
        explanation:
          'A sloppy iteration is a rerun you pay for twice — once in tokens, once in your own review time.',
      },
    ],
  },
] as const;

const CLOSING_RULES = [
  { title: 'Think Before You Act', icon: Lightbulb },
  { title: 'Verify Before You Ship', icon: SearchCheck },
  { title: "Wisdom Isn't Automated", icon: GraduationCap },
  { title: 'Move Quick, Not Careless', icon: Zap },
] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 48 : -48,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -48 : 48,
    opacity: 0,
  }),
};

function OldRulesNewTools() {
  const [[slideIndex, direction], setSlide] = useState([0, 0]);

  const slides = [
    {
      id: 'hero-intro',
      content: (
        <div className="orn-slide-inner orn-slide-hero-intro">
          <div className="orn-hero-block">
            <div className="orn-icon-row" aria-hidden="true">
              <span className="orn-icon-badge">
                <BookOpen size={22} />
              </span>
              <span className="orn-icon-badge">
                <Sparkles size={22} />
              </span>
              <span className="orn-icon-badge">
                <Wrench size={22} />
              </span>
            </div>
            <h1 className="orn-hero-title">Old Rules. New Tools.</h1>
            <p className="orn-hero-subtitle">
              Ground rules for working with AI — from someone who remembers doing it without AI.
            </p>
            <p className="orn-hero-tags">
              <GraduationCap size={16} aria-hidden="true" />
              AI Personal Learning
            </p>
          </div>
          <div className="orn-speaker-card">
            <div className="orn-headshot">
              <img src="/images/logo.png" alt="Basant Kumar Verma" />
            </div>
            <div className="orn-speaker-text">
              <h2 className="orn-speaker-name">Basant Kumar Verma</h2>
              <p className="orn-speaker-role">Director, Delivery &amp; Innovation</p>
              <p className="orn-speaker-bio">
                I lead delivery at CRMified, a Salesforce consulting and development company. I work
                across Salesforce, automation, and AI-assisted engineering workflows. I&apos;ve been
                building on enterprise platforms long enough to have watched a few
                &quot;revolutionary&quot; tools come and go — and I believe the fundamentals
                haven&apos;t changed nearly as much as the tooling around them has.
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'why',
      content: (
        <div className="orn-slide-inner">
          <article className="orn-rule-group orn-why-card">
            <div className="orn-rule-group-header">
              <span className="orn-rule-icon" aria-hidden="true">
                <Brain size={22} />
              </span>
              <h2 className="orn-rule-title">
                Too Much to Remember. Not Enough to Recall.
              </h2>
            </div>
            <p className="orn-section-kicker orn-why-kicker">A few old lines, kept short on purpose.</p>
            <div className="orn-prose">
              <p>Before getting into it, here&apos;s why this is built the way it is.</p>
              <p>
                By now you&apos;ve heard the big words — RAG, Agents, MCP, Vector Databases, whatever
                term is trending this month. This isn&apos;t one more of those.
              </p>
              <p>
                Here&apos;s the real problem. Our minds are being asked to digest more new information,
                more often, than at any other point in human history. Content doesn&apos;t just grow
                anymore — it refreshes constantly, faster than we can file it away. Our neurons simply
                haven&apos;t caught up to that pace. We weren&apos;t built to hold onto this much churn.
              </p>
              <p>
                So instead of adding more heavy terminology that gets forgotten by tomorrow morning,
                this takes a different approach: leaving behind something small — a pointer, sitting
                quietly at the back of the mind — that brings the whole thought back the moment you see
                it again. You&apos;ve noticed this yourself: the reels and shorts that actually stay
                with you aren&apos;t the ones packed with information. They&apos;re the ones that cut to
                one sharp moment and let your own memory do the rest.
              </p>
              <p>
                That&apos;s the idea here. Not new frameworks — old, simple lines. The kind you already
                know, maybe the kind your grandfather used on you once — shown to still hold, word for
                word, in the AI era. Call it old school. But it&apos;s better to walk away remembering
                four short lines than forgetting four new ones.
              </p>
              <p>So here&apos;s what&apos;s next — four old ideas, and exactly where they still apply.</p>
            </div>
          </article>
        </div>
      ),
    },
    {
      id: 'rules-intro',
      content: (
        <div className="orn-slide-inner orn-slide-center">
          <div className="orn-section-icon" aria-hidden="true">
            <Scale size={28} />
          </div>
          <h2 className="orn-section-heading">Four Old Rules, Still Applying</h2>
          <p className="orn-section-kicker">
            Old-school judgment, applied to the way we build in 2026.
          </p>
          <div className="orn-rules-preview" aria-hidden="true">
            {RULE_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.number} className="orn-rules-preview-item">
                  <span className="orn-icon-badge">
                    <Icon size={20} />
                  </span>
                  <span>{group.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      ),
    },
    ...RULE_GROUPS.map((group) => {
      const GroupIcon = group.icon;
      return {
        id: `rule-${group.number}`,
        content: (
          <div className="orn-slide-inner">
            <article className="orn-rule-group">
              <div className="orn-rule-group-header">
                <span className="orn-rule-icon" aria-hidden="true">
                  <GroupIcon size={22} />
                </span>
                <span className="orn-rule-number">{group.number}</span>
                <h3 className="orn-rule-title">{group.title}</h3>
              </div>
              <div className="orn-quotes">
                {group.quotes.map((quote) => {
                  const QuoteIcon = quote.icon;
                  return (
                    <blockquote key={quote.text} className="orn-quote">
                      <div className="orn-quote-header">
                        <span className="orn-quote-icon" aria-hidden="true">
                          <QuoteIcon size={16} />
                        </span>
                        <p className="orn-quote-text">&ldquo;{quote.text}&rdquo;</p>
                      </div>
                      <p className="orn-quote-explanation">{quote.explanation}</p>
                    </blockquote>
                  );
                })}
              </div>
            </article>
          </div>
        ),
      };
    }),
    {
      id: 'closing',
      content: (
        <div className="orn-slide-inner orn-slide-center">
          <div className="orn-section-icon" aria-hidden="true">
            <CheckCircle2 size={28} />
          </div>
          <h2 className="orn-closing-title">Same Judgment. Faster Tools.</h2>
          <p className="orn-section-kicker">Pick one rule. Start there this week.</p>
          <ul className="orn-closing-list">
            {CLOSING_RULES.map((rule) => {
              const Icon = rule.icon;
              return (
                <li key={rule.title}>
                  <span className="orn-closing-icon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  {rule.title}
                </li>
              );
            })}
          </ul>
          <p className="orn-thank-you">
            <Quote size={18} aria-hidden="true" />
            Thank you.
          </p>
        </div>
      ),
    },
  ];

  const totalSlides = slides.length;
  const isFirst = slideIndex === 0;
  const isLast = slideIndex === totalSlides - 1;

  const goTo = useCallback(
    (nextIndex: number, dir: number) => {
      if (nextIndex < 0 || nextIndex >= totalSlides) return;
      setSlide([nextIndex, dir]);
    },
    [totalSlides]
  );

  const next = useCallback(() => {
    if (!isLast) goTo(slideIndex + 1, 1);
  }, [goTo, isLast, slideIndex]);

  const prev = useCallback(() => {
    if (!isFirst) goTo(slideIndex - 1, -1);
  }, [goTo, isFirst, slideIndex]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = 'Old Rules. New Tools. — Basant Kumar Verma';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === ' ' || event.key === 'PageDown') {
        event.preventDefault();
        next();
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        prev();
      } else if (event.key === 'Home') {
        event.preventDefault();
        goTo(0, -1);
      } else if (event.key === 'End') {
        event.preventDefault();
        goTo(totalSlides - 1, 1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goTo, next, prev, totalSlides]);

  const handleDeckClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button, a, .orn-slide-nav, .orn-progress')) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    if (clickX < rect.width * 0.28) {
      prev();
    } else {
      next();
    }
  };

  return (
    <div className="App orn-slideshow-app">
      <FloatingIcons />
      <Header />

      <main
        className="orn-deck"
        onClick={handleDeckClick}
        role="region"
        aria-roledescription="carousel"
        aria-label="Old Rules. New Tools. presentation"
      >
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.section
            key={slides[slideIndex].id}
            className="orn-slide"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: 'easeOut' }}
            aria-live="polite"
          >
            <div className="container orn-slide-container">{slides[slideIndex].content}</div>
          </motion.section>
        </AnimatePresence>

        <div className="orn-slide-nav" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="orn-nav-btn"
            onClick={prev}
            disabled={isFirst}
            aria-label="Previous slide"
          >
            <ChevronLeft size={22} />
          </button>
          <span className="orn-slide-counter">
            {slideIndex + 1} / {totalSlides}
          </span>
          <button
            type="button"
            className="orn-nav-btn"
            onClick={next}
            disabled={isLast}
            aria-label="Next slide"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="orn-progress" onClick={(e) => e.stopPropagation()} role="tablist" aria-label="Slides">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={index === slideIndex}
              aria-label={`Go to slide ${index + 1}`}
              className={`orn-progress-dot${index === slideIndex ? ' active' : ''}`}
              onClick={() => goTo(index, index > slideIndex ? 1 : -1)}
            />
          ))}
        </div>

        {!isLast && <p className="orn-click-hint">Click or press → for next slide</p>}
      </main>
    </div>
  );
}

export default OldRulesNewTools;
